import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import {
  ClientRecordTemplate,
  getAll as getTemplates,
} from "../../services/clientRecordTemplateService";

const TEMPLATE_PAGE_SIZE = 100;

type AnamnesisPromptModalProps = {
  isOpen: boolean;
  clientName: string;
  onLater: () => void;
  onConfirm: (templateId: number | null) => void;
};

const AnamnesisPromptModal = ({
  isOpen,
  clientName,
  onLater,
  onConfirm,
}: AnamnesisPromptModalProps) => {
  const { t } = useTranslation();

  const [templates, setTemplates] = useState<ClientRecordTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const activeTemplates = useMemo(
    () => templates.filter((template) => template.is_active),
    [templates]
  );

  const showTemplateSelect = activeTemplates.length > 1;

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);

    try {
      const response = await getTemplates({
        page: 1,
        per_page: TEMPLATE_PAGE_SIZE,
        is_active: true,
      });

      setTemplates(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("clients.anamnesis.toast.loadTemplatesError"));
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [t]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSelectedTemplateId("");
    loadTemplates();
  }, [isOpen, loadTemplates]);

  useEffect(() => {
    if (activeTemplates.length === 1) {
      setSelectedTemplateId(String(activeTemplates[0].id));
    }
  }, [activeTemplates]);

  const handleConfirm = () => {
    if (showTemplateSelect && !selectedTemplateId) {
      toast.error(t("clients.anamnesis.validation.templateRequired"));
      return;
    }

    const templateId =
      activeTemplates.length === 1
        ? activeTemplates[0].id
        : selectedTemplateId
          ? Number(selectedTemplateId)
          : null;

    onConfirm(templateId);
  };

  return (
    <Modal isOpen={isOpen} toggle={onLater} centered>
      <ModalHeader toggle={onLater}>
        {t("studio.clients.anamnesis_prompt.title")}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted mb-0">
          {t("studio.clients.anamnesis_prompt.message", { name: clientName })}
        </p>

        {loadingTemplates ? (
          <div className="d-flex justify-content-center py-3">
            <Spinner color="primary" size="sm" />
          </div>
        ) : showTemplateSelect ? (
          <div className="mt-3">
            <Label htmlFor="anamnesis-prompt-template">
              {t("studio.clients.anamnesis_prompt.select_template")}
            </Label>
            <Input
              id="anamnesis-prompt-template"
              type="select"
              value={selectedTemplateId}
              onChange={(event) => setSelectedTemplateId(event.target.value)}
            >
              <option value="">{t("clients.anamnesis.fields.templatePlaceholder")}</option>
              {activeTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.title}
                </option>
              ))}
            </Input>
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onLater}>
          {t("studio.clients.anamnesis_prompt.later")}
        </Button>
        <Button color="primary" onClick={handleConfirm} disabled={loadingTemplates}>
          {t("studio.clients.anamnesis_prompt.yes")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AnamnesisPromptModal;
